import {
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError
} from '@reduxjs/toolkit/query';
import customFetchBase from '@/app/common/utils/customFetchBase';
import { getTrackerAiApiBase } from './config';
import { getTrackerDomainSlug, singleAddressParam } from './trackerDomainBody';

interface TrackerAiEnvelope<T = unknown> {
  code: number;
  msg: string;
  data: T;
}

const EXPIRED_PANEL_MESSAGE =
  'طرح شما به پایان رسیده است. برای ادامه، اشتراک را تمدید کنید.';

function userFacingMessage(msg: string, status: number): string {
  if (msg === 'expired_panel') return EXPIRED_PANEL_MESSAGE;
  return msg || `Request failed (${status})`;
}

function withDomainBody(args: string | FetchArgs): FetchArgs {
  const fetchArgs: FetchArgs =
    typeof args === 'string' ? { url: args } : { ...args };
  const method = (fetchArgs.method ?? 'GET').toUpperCase();
  // fetch() refuses a GET body. Reads that need domain_slug are POST on the API.
  if (method === 'GET') fetchArgs.method = 'POST';

  const current = fetchArgs.body;
  const body: Record<string, unknown> =
    current && typeof current === 'object' && !Array.isArray(current)
      ? { ...(current as Record<string, unknown>) }
      : {};

  const slug = getTrackerDomainSlug();
  if (slug) body.domain_slug = slug;

  const single = singleAddressParam(fetchArgs.url);
  if (single && (body[single.key] == null || body[single.key] === '')) {
    body[single.key] = single.value;
  }

  fetchArgs.body = body;
  return fetchArgs;
}

/** RTK Query baseQuery for Tracker AI API (`{ code, msg, data }`). */
export const trackerAiBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await customFetchBase(
    getTrackerAiApiBase(),
    withDomainBody(args),
    api,
    extraOptions,
  );

  // HTTP error (e.g. 409): normalize Tracker AI envelope `{ code, msg, data }`.
  if (result.error) {
    const errData = result.error.data;
    if (
      errData &&
      typeof errData === 'object' &&
      'code' in errData &&
      'msg' in errData
    ) {
      const envelope = errData as TrackerAiEnvelope;
      const status =
        typeof envelope.code === 'number'
          ? envelope.code
          : typeof result.error.status === 'number'
            ? result.error.status
            : 500;
      return {
        error: {
          status,
          data: {
            message: userFacingMessage(envelope.msg, status),
            data: envelope.data
          }
        }
      };
    }
    return result;
  }

  const envelope = result.data as TrackerAiEnvelope | null | undefined;
  if (
    envelope &&
    typeof envelope === 'object' &&
    'code' in envelope &&
    'data' in envelope
  ) {
    if (typeof envelope.code === 'number' && envelope.code >= 400) {
      return {
        error: {
          status: envelope.code,
          data: {
            message: userFacingMessage(envelope.msg, envelope.code),
            data: envelope.data
          }
        }
      };
    }
    return { data: envelope.data };
  }

  return { data: result.data };
};
